import { call, put, select, takeLatest } from 'redux-saga/effects';
import { stringify } from 'query-string';

import { buildAllPtcHeaders } from 'helpers/ptcHeaders';
import {
  BEGIN_FETCH_SYMPHONY_SEARCH_COMPONENTS,
  FETCH_SYMPHONY_SEARCH_COMPONENTS_ERROR,
  RECEIVE_SYMPHONY_SEARCH_COMPONENTS
} from 'store/ducks/search/types';
import { classifyUpstreamResult, getSearchContentTimeoutMs, reportDegradation, withTimeout } from 'helpers/upstreamDegradation';
import { selectZcsConfig } from 'selectors/environment';
import { FetchError } from 'middleware/fetchErrorMiddleware';
import timedFetch from 'middleware/timedFetch';
import { addClientHeaders, fetchOpts } from 'apis/mafia/common';
import { ZSO_STRING_RE } from 'common/regex';

export function getSymphonySearchComponents(
  { url },
  { term, path },
  credentials = {},
  request = {},
  ptcHeaders = {},
  fetcher = timedFetch('getSymphonySearchComponents'),
  signal
) {
  const queryObj = {
    pageName: term,
    pageLayout: 'search'
  };

  if (path) {
    if (path.includes('.zso')) {
      queryObj['ProductSearchZso'] = path;
    } else {
      queryObj['ProductSearchTerm'] = path;
    }
  }
  const query = stringify(queryObj);
  const reqUrl = `${url}/zcs/getSlots?${query}`;
  const headers = { ...addClientHeaders(request), ...ptcHeaders };
  return fetcher(reqUrl, fetchOpts({ headers, signal }, credentials));
}

export function parseSymphonySearchComponentsResponse(response) {
  if (!response.ok) {
    throw new FetchError(response.url, response.status, response.statusText);
  }

  const contentType = response.headers?.get('content-type');
  if (contentType) {
    const mimeType = contentType.split(';')[0].trim().toLowerCase();
    if (mimeType !== 'application/json' && !mimeType.endsWith('+json')) {
      throw new SyntaxError(`Expected JSON from ZCS, received ${mimeType}`);
    }
  }

  return response.json();
}

export function* workFetchSymphonySearchComponents() {
  try {
    const state = yield select();
    const {
      client: { request },
      cookies,
      filters: { term, executedSearchUrl, selected }
    } = state;
    const zcsConfig = yield select(selectZcsConfig);
    const zsoString = ZSO_STRING_RE.exec(executedSearchUrl)?.[0];

    const catalogContext = {
      gender: selected?.singleSelects?.txAttrFacet_Gender?.[0] ?? null,
      productType: selected?.singleSelects?.zc1?.[0] ?? null,
      category: selected?.singleSelects?.zc2?.[0] ?? null
    };
    const ptcHeaders = buildAllPtcHeaders(state, catalogContext);

    const response = yield call(
      withTimeout,
      signal =>
        getSymphonySearchComponents(
          zcsConfig,
          { term: term || zsoString || 'null', path: executedSearchUrl },
          cookies,
          request,
          ptcHeaders,
          undefined,
          signal
        ),
      getSearchContentTimeoutMs()
    );
    const data = yield call(parseSymphonySearchComponentsResponse, response);
    yield put({
      type: RECEIVE_SYMPHONY_SEARCH_COMPONENTS,
      payload: { data, term }
    });
  } catch (error) {
    yield put({
      type: FETCH_SYMPHONY_SEARCH_COMPONENTS_ERROR,
      payload: { error }
    });
    // A customer-initiated navigation cancel (AbortError) is not a degradation,
    // so the slot is cleared without emitting a failure metric.
    if (classifyUpstreamResult(error) === 'degraded') {
      yield call(reportDegradation, {
        service: 'ZCS',
        operation: 'SymphonySearchComponentsLoad',
        region: 'SymphonySearchComponents',
        error
      });
    }
  }
}

export function* watchFetchSymphonySearchComponents() {
  yield takeLatest(BEGIN_FETCH_SYMPHONY_SEARCH_COMPONENTS, workFetchSymphonySearchComponents);
}

export default [watchFetchSymphonySearchComponents];
